import React, { useState } from 'react';
import { DB, canCustomerOffer } from '../data/database';
import { writeAuditLog } from '../utils/audit';
import { CUT_TYPES, exportCSV } from '../data/constants';
import { PART_TYPES } from '../data/partTypes';
import { formatPrice } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { CarIcon, EngineIcon, PartsIcon, WhatsAppIcon, XIcon, SearchIcon, CheckIcon, PackageIcon, DollarSignIcon } from './Icons';
import { logActivity, Notifications } from '../data/notifications';
import DetailModal from './modals/DetailModal';
import PartsRequestModal from './modals/PartsRequestModal';

const OFFERABLE_PARTS = PART_TYPES.filter(pt => pt.tier === 'part');

const CUSTOMER_STATUS_LABEL = {
  available: 'Available',
  japan_yard: 'In Japan Yard',
  in_transit: 'In Transit',
  loading: 'Loading — unavailable',
  sold: 'Sold',
  assigned: 'On hold',
  reserved: 'Reserved',
  cutting: 'Being dismantled',
  dismantled: 'Dismantled',
  converted: 'No longer available',
  delivered: 'Delivered',
  sold_awaiting_payment: 'Sold',
};

function getCustomerStatusLabel(item, userId) {
  if (item.status === 'sold' || item.status === 'sold_awaiting_payment') {
    if (item.assignedTo === userId) return 'Yours';
    if (item.status === 'sold_awaiting_payment') return 'Available';
    return 'Sold';
  }
  return CUSTOMER_STATUS_LABEL[item.status] || item.status;
}

// ── PDF export ────────────────────────────────────────────────
function exportStockPDF(items, customerName, tabLabel) {
  if (!items.length) { alert('No data to export.'); return; }
  const rows = items.map(i => `<tr><td>${i.sku||''}</td><td>${i.chassis||''}</td><td>${i.make?`${i.make} ${i.model}`:i.engineName||i.partName||''}</td><td>${i.make?'Vehicle':i.engineName?'Engine':'Part'}</td><td style="text-transform:capitalize">${(i.status||'').replace(/_/g,' ')}</td><td>${i.year||''}</td><td>${i.city||i.port||''}</td></tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${customerName} — ${tabLabel}</title><style>body{font-family:Arial,sans-serif;padding:28px;color:#111;font-size:12px}h1{font-size:20px;margin:0 0 4px}.sub{color:#666;font-size:11px;margin-bottom:20px}table{width:100%;border-collapse:collapse}th{background:#1a3a6b;color:#fff;text-align:left;padding:7px 10px;font-size:11px}td{padding:6px 10px;border-bottom:1px solid #e0e0e0}tr:nth-child(even) td{background:#f6f8fb}@media print{body{padding:0}}</style></head><body><h1>BIG STAR — ${customerName}</h1><div class="sub">${tabLabel} · ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</div><table><tr><th>SKU</th><th>Chassis</th><th>Name</th><th>Type</th><th>Status</th><th>Year</th><th>Location</th></tr>${rows}</table><script>window.onload=()=>{window.print()}<\/script></body></html>`;
  let iframe = document.getElementById('print-frame');
  if (!iframe) { iframe = document.createElement('iframe'); iframe.id = 'print-frame'; iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;'; document.body.appendChild(iframe); }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { try { iframe.contentWindow.print(); } catch {} };
}

function MyStock({ user, currency, lang }) {
  const { t } = useLanguage();
  const [mainTab, setMainTab]             = useState('stock');
  const [tab, setTab]                     = useState('all');
  const [search, setSearch]               = useState('');
  const [dlOpen, setDlOpen]               = useState(false);
  const [checkedIds, setCheckedIds]       = useState(new Set());
  const [showReview, setShowReview]       = useState(false);  // 'selection' | 'fob' | false
  const [reviewNote, setReviewNote]       = useState('');
  const [submitted, setSubmitted]         = useState('');      // success message
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [cancelTarget, setCancelTarget]   = useState(null);
  const [detail, setDetail]               = useState(null);
  const [partsReqVehicle, setPartsReqVehicle] = useState(null);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [, forceUpdate]                   = useState();
  const [offerAmounts, setOfferAmounts]   = useState({});
  const [offerModal, setOfferModal]       = useState(null);
  const [offerParts, setOfferParts]       = useState([]);
  const [offerSaving, setOfferSaving]     = useState(false);
  const [offerCur, setOfferCur]           = useState(currency || 'USD');
  const [offerError, setOfferError]       = useState('');

  // ── Data — customer_stock entries ONLY (wishlist) ──
  const customerStock = (DB.get('customerStock') || []).filter(s => s.customerId === user.id);
  const allVehicles = DB.get('vehicles') || [];
  const allEngines  = DB.get('engines') || [];
  const allParts    = DB.get('spareParts') || [];
  const myUnitIds = new Set(customerStock.map(s => s.unitId));

  const allItems = [
    ...allVehicles.filter(v => myUnitIds.has(v.id)).map(v => ({ ...v, _type: 'vehicle', _dbKey: 'vehicles' })),
    ...allEngines.filter(e => myUnitIds.has(e.id)).map(e => ({ ...e, _type: 'engine', _dbKey: 'engines' })),
    ...allParts.filter(p => myUnitIds.has(p.id)).map(p => ({ ...p, _type: 'part', _dbKey: 'spareParts' })),
  ];

  const myOrders = (DB.get('selectionOrders') || []).filter(o => o.customerId === user.id);
  const allQuotes = DB.get('priceQuotes') || [];
  const myQuotes = allQuotes.filter(q => q.customerId === user.id || q.requestedBy === user.id);
  const invoices = DB.get('invoices') || [];
  const containers = DB.get('containers') || [];
  const myOffers = (DB.get('offers') || []).filter(o => o.customerId === user.id);

  const getActiveOffer = (unitId) =>
    myOffers.find(o => o.unitId === unitId && o.status !== 'withdrawn' && o.status !== 'declined');

  const availableCurrencies = (() => {
    const custs = DB.get('customers') || [];
    const set = new Set(custs.map(c => c.currency).filter(Boolean));
    if (currency) set.add(currency);
    return set.size > 0 ? [...set].sort() : ['USD'];
  })();

  // ── Categorize units ──
  const lockedMap = {};
  myOrders.forEach(o => {
    if (['submitted', 'under_review', 'approved', 'partially_approved', 'paid', 'shipped'].includes(o.status)) {
      (o.units || []).forEach(u => {
        if (u.status !== 'rejected') {
          lockedMap[u.unit_id] = { orderId: o.id, orderStatus: o.status, unitStatus: u.status, submittedAt: o.submittedAt || o.createdAt };
        }
      });
    }
  });

  const availableUnits = allItems.filter(i => !lockedMap[i.id]);
  const inReviewUnits  = allItems.filter(i => lockedMap[i.id] && lockedMap[i.id].unitStatus === 'pending');
  const approvedUnits  = allItems.filter(i => lockedMap[i.id] && lockedMap[i.id].unitStatus === 'approved');

  // ── Quote status for a unit ──
  const getUnitQuoteStatus = (unitId) => {
    for (const q of myQuotes) {
      if (q.status === 'expired') continue;
      const units = q.units || [];
      const match = units.find(u => u.unit_id === unitId);
      if (match) {
        if (q.status === 'approved' && match.status === 'approved') {
          const fob = match.pricing?.fob_price || match.fob_price || 0;
          const expired = q.expires_at && new Date(q.expires_at) < new Date();
          return { status: expired ? 'expired' : 'priced', fob, quoteId: q.id, expires_at: q.expires_at };
        }
        if (['pending', 'in_progress', 'pending_approval', 'revision_needed'].includes(q.status)) return { status: 'requested', quoteId: q.id };
      }
      if (q.unitId === unitId) {
        if (q.status === 'quoted') {
          const expired = q.expires_at && new Date(q.expires_at) < new Date();
          return { status: expired ? 'expired' : 'priced', fob: q.fobPriceUsd || q.fob_price_usd || 0, quoteId: q.id, expires_at: q.expires_at };
        }
        if (['pending', 'in_progress'].includes(q.status)) return { status: 'requested', quoteId: q.id };
      }
    }
    return null;
  };

  // Helper: format time remaining for expiry countdown
  const formatTimeLeft = (expiresAt) => {
    if (!expiresAt) return '';
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return 'EXPIRED';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };

  // ── Filtering ──
  const tabItems = tab === 'all' ? availableUnits
    : tab === 'vehicles' ? availableUnits.filter(i => i._type === 'vehicle')
    : tab === 'engines'  ? availableUnits.filter(i => i._type === 'engine')
    : availableUnits.filter(i => i._type === 'part');

  const filteredItems = tabItems.filter(item => {
    if (search) {
      const q = search.toLowerCase();
      const fields = [item.make, item.model, item.engineName, item.partName, item.sku, item.chassis, item.year?.toString()].filter(Boolean).join(' ').toLowerCase();
      if (!fields.includes(q)) return false;
    }
    return true;
  });

  // ── Helpers ──
  const getName = (item) => item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || 'Unknown';
  const getDbKey = (_type) => _type === 'vehicle' ? 'vehicles' : _type === 'engine' ? 'engines' : 'spareParts';
  const getIcon = (item) => item._type === 'vehicle' ? <CarIcon /> : item._type === 'engine' ? <EngineIcon /> : <PartsIcon />;

  // FOB price is PER CUSTOMER — read from approved quote, NOT from unit record
  const getFob = (item) => {
    const qs = getUnitQuoteStatus(item.id);
    if (qs?.status === 'priced' && qs.fob > 0) return qs.fob;
    return 0; // No approved quote = no price for this customer
  };

  const checkedItems = allItems.filter(i => checkedIds.has(i.id));
  const checkedTotal = checkedItems.reduce((s, i) => s + getFob(i), 0);
  const unpricedCount = checkedItems.filter(i => !getFob(i)).length;

  // ── Actions ──
  const toggleCheck = (id, e) => {
    e.stopPropagation();
    setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const openRemoveModal = (item, e) => { e.stopPropagation(); setCancelTarget(item); };

  const confirmRemove = () => {
    if (!cancelTarget) return;
    const stock = DB.get('customerStock') || [];
    DB.set('customerStock', stock.filter(s => !(s.customerId === user.id && s.unitId === cancelTarget.id)));

    writeAuditLog({
      action: 'customer_stock.unit_removed',
      entityType: 'customer_stock',
      entityId: cancelTarget.id,
      entityLabel: `${user.name} removed ${cancelTarget.sku || cancelTarget.id}`,
      details: {
        customer_id: user.id,
        customer_name: user.name,
        unit_id: cancelTarget.id,
        unit_sku: cancelTarget.sku,
        unit_name: cancelTarget.make ? `${cancelTarget.make} ${cancelTarget.model}` : cancelTarget.partName || cancelTarget.engineName || '',
        removed_by: 'customer',
      },
    });

    setCancelTarget(null); setCheckedIds(prev => { const n = new Set(prev); n.delete(cancelTarget.id); return n; }); forceUpdate({});
  };

  // ── BATCH FOB REQUEST ──
  const handleRequestFobPrices = () => {
    if (checkedItems.length === 0) return;

    // Check if there's an existing pending batch quote for this customer
    const existingQuote = allQuotes.find(q =>
      (q.customerId === user.id || q.requestedBy === user.id) &&
      ['pending', 'in_progress'].includes(q.status) &&
      Array.isArray(q.units)
    );

    const unitEntries = checkedItems.map(item => ({
      unit_id: item.id,
      unit_type: item._type,
      unit_sku: item.sku,
      unit_description: getName(item) + (item.year ? ` (${item.year})` : ''),
      fob_price: null,
      pricing: null,
      status: 'pending',
      accountant_notes: '',
      admin_approved: false,
    }));

    if (existingQuote) {
      // Merge new units into existing quote (avoid duplicates)
      const existingUnitIds = new Set((existingQuote.units || []).map(u => u.unit_id));
      const newUnits = unitEntries.filter(u => !existingUnitIds.has(u.unit_id));
      if (newUnits.length === 0) {
        setSubmitted('These units are already in your pending price request.');
        setShowReview(false); setCheckedIds(new Set());
        return;
      }
      const updated = { ...existingQuote, units: [...(existingQuote.units || []), ...newUnits] };
      DB.set('priceQuotes', allQuotes.map(q => q.id === existingQuote.id ? updated : q));
    } else {
      // Create new batch quote
      const quote = {
        id: DB.genId('PQ'),
        customerId: user.id,
        customerName: user.name,
        requestedBy: user.id,
        requestedByName: user.name,
        status: 'pending',
        units: unitEntries,
        customer_notes: reviewNote.trim(),
        assigned_to: null,
        createdAt: new Date().toISOString().split('T')[0],
        submittedAt: new Date().toISOString(),
      };
      DB.set('priceQuotes', [...allQuotes, quote]);
    }

    // Notify accountant + admin
    Notifications.add('price_updated', 'Price Quote Request',
      `${user.name} requested FOB prices for ${checkedItems.length} unit${checkedItems.length !== 1 ? 's' : ''}`, {});

    setSubmitted('Price request submitted! You will be notified when prices are ready.');
    setShowReview(false); setReviewNote(''); setCheckedIds(new Set()); forceUpdate({});
  };

  // ── SUBMIT FINAL SELECTION ──
  const handleSubmitSelection = () => {
    if (checkedItems.length === 0) return;
    // Block submission if any prices have expired
    const expiredUnits = checkedItems.filter(i => {
      const qs = getUnitQuoteStatus(i.id);
      return qs?.status === 'expired';
    });
    if (expiredUnits.length > 0) {
      alert('Some prices have expired. Request new quotes before submitting:\n' +
        expiredUnits.map(u => `- ${getName(u)}: price expired`).join('\n'));
      return;
    }
    const orderId = DB.genId('SO');
    const order = {
      id: orderId, customerId: user.id, customerName: user.name, status: 'submitted',
      units: checkedItems.map(item => ({
        unit_id: item.id, unit_type: item._type, unit_sku: item.sku,
        unit_description: getName(item) + (item.year ? ` (${item.year})` : ''),
        fob_price_usd: getFob(item) || null, status: 'pending',
        admin_notes: '', modified_price: null, approved_by: null, approved_at: null,
      })),
      totalUnits: checkedItems.length,
      totalValueUsd: checkedTotal,
      customerNotes: reviewNote.trim(),
      submittedAt: new Date().toISOString(),
      createdAt: new Date().toISOString().split('T')[0],
    };
    checkedItems.forEach(item => {
      const list = DB.get(item._dbKey) || [];
      DB.set(item._dbKey, list.map(u => u.id === item.id ? { ...u, selectionOrderId: orderId } : u));
    });
    DB.set('selectionOrders', [...(DB.get('selectionOrders') || []), order]);
    logActivity.requestNew(user.name, `Selection Order — ${checkedItems.length} units, $${checkedTotal.toLocaleString()}`);

    // Auto-create price quote for accountant — so pricing can start immediately
    const existingPQ = allQuotes.find(q =>
      (q.customerId === user.id || q.requestedBy === user.id) &&
      ['pending', 'in_progress'].includes(q.status) && Array.isArray(q.units)
    );
    const pqUnits = checkedItems.map(item => ({
      unit_id: item.id, unit_type: item._type, unit_sku: item.sku,
      unit_description: getName(item) + (item.year ? ` (${item.year})` : ''),
      fob_price: null, pricing: null, status: 'pending', accountant_notes: '', admin_approved: false,
    }));
    if (existingPQ) {
      // Merge new units into existing quote (skip duplicates)
      const existingIds = new Set((existingPQ.units || []).map(u => u.unit_id));
      const newUnits = pqUnits.filter(u => !existingIds.has(u.unit_id));
      if (newUnits.length > 0) {
        const updatedPQ = { ...existingPQ, units: [...(existingPQ.units || []), ...newUnits] };
        DB.set('priceQuotes', (DB.get('priceQuotes') || []).map(q => q.id === existingPQ.id ? updatedPQ : q));
      }
    } else {
      const pq = {
        id: DB.genId('PQ'), customerId: user.id, customerName: user.name,
        requestedBy: user.id, requestedByName: user.name, status: 'pending',
        units: pqUnits, customer_notes: `Auto-generated from Selection Order #${orderId}`,
        sourceOrderId: orderId, createdAt: new Date().toISOString().split('T')[0], submittedAt: new Date().toISOString(),
      };
      DB.set('priceQuotes', [...(DB.get('priceQuotes') || []), pq]);
    }
    // Notify accountant
    Notifications.add('price_updated', 'Selection Needs Pricing',
      `${user.name} submitted selection — ${checkedItems.length} units need pricing. Open Price Quotes to set FOB prices.`, {});

    setCheckedIds(new Set()); setShowReview(false); setReviewNote('');
    setSubmitted('Your selection has been submitted for review. You will be notified once approved.');
    setMainTab('orders'); setExpandedOrder(orderId); forceUpdate({});
  };

  const cancelOrder = (orderId) => {
    const order = myOrders.find(o => o.id === orderId);
    if (!order || order.status !== 'submitted') return;
    if (!window.confirm('Cancel this order? All units will return to your available stock.')) return;
    (order.units || []).forEach(u => {
      ['vehicles', 'engines', 'spareParts'].forEach(key => {
        const list = DB.get(key) || [];
        DB.set(key, list.map(i => i.id === u.unit_id ? { ...i, selectionOrderId: '' } : i));
      });
    });
    const allOrders = DB.get('selectionOrders') || [];
    DB.set('selectionOrders', allOrders.map(o => o.id === orderId ? { ...o, status: 'cancelled' } : o));
    forceUpdate({});
  };

  // ── Offer actions ──
  const submitOffer = async (item, offerType, partsRequested) => {
    if (offerSaving) return;
    const amt = parseFloat(offerAmounts[item.id]);
    if (!amt || amt <= 0) return;
    setOfferSaving(true);
    setOfferError('');
    const now = new Date().toISOString();
    const offer = {
      id: DB.genId('OFR'),
      customerId: user.id,
      unitId: item.id,
      unitType: item._type,
      offerType,
      partsRequested: offerType === 'parts' ? partsRequested : [],
      offerAmount: amt,
      offerCurrency: offerCur,
      status: 'pending',
      counterAmount: null,
      counterCurrency: null,
      counterBy: null,
      customerResponse: null,
      history: [{ by: user.id, role: 'customer', action: 'offer', amount: amt, at: now }],
      notes: '',
      createdAt: now,
      updatedAt: now,
    };
    const prev = DB.get('offers') || [];
    const result = await DB.setAsync('offers', [...prev, offer]);
    if (result?.error) {
      DB.set('offers', prev);
      setOfferError("Couldn't send offer — try again.");
    } else {
      setOfferAmounts(p => { const n = { ...p }; delete n[item.id]; return n; });
      setOfferModal(null);
      setOfferParts([]);
      Notifications.add('offer_submitted', 'New Customer Offer',
        `${user.name} offered ${offerCur} ${amt.toLocaleString()} for ${item.sku || item.id} (${offerType})`, {});
    }
    setOfferSaving(false);
    forceUpdate({});
  };

  const withdrawOffer = async (offerId) => {
    if (offerSaving) return;
    setOfferSaving(true);
    const all = DB.get('offers') || [];
    const now = new Date().toISOString();
    const updated = all.map(o => {
      if (o.id !== offerId) return o;
      return { ...o, status: 'withdrawn', updatedAt: now,
        history: [...(o.history || []), { by: user.id, role: 'customer', action: 'withdraw', at: now }] };
    });
    await DB.setAsync('offers', updated);
    setOfferSaving(false);
    forceUpdate({});
  };

  const respondToCounter = async (offerId, accept) => {
    if (offerSaving) return;
    setOfferSaving(true);
    const all = DB.get('offers') || [];
    const now = new Date().toISOString();
    const updated = all.map(o => {
      if (o.id !== offerId) return o;
      return { ...o,
        customerResponse: accept ? 'accepted' : 'declined',
        status: accept ? 'agreed' : 'declined',
        updatedAt: now,
        history: [...(o.history || []), { by: user.id, role: 'customer', action: accept ? 'accept' : 'decline', at: now }],
      };
    });
    await DB.setAsync('offers', updated);
    setOfferSaving(false);
    forceUpdate({});
  };

  const getContainerInfo = (unitId) => containers.find(c => (c.units || []).includes(unitId));
  const getInvoiceForOrder = (orderId) => invoices.find(i => i.linkedSelectionOrderId === orderId);

  // ── Price badge for a unit ──
  // Price badge — reads from customer's quote, NOT unit record
  const renderPriceBadge = (item) => {
    // Check if unit is sold to someone else
    if (item.status === 'sold' && item.assignedTo && item.assignedTo !== user.id) {
      return <span style={{ color: 'var(--red)', fontSize: 10, fontWeight: 600 }}>SOLD ❌</span>;
    }
    const qs = getUnitQuoteStatus(item.id);
    if (qs?.status === 'expired') return (
      <div>
        <span style={{ color: 'var(--red)', fontSize: 10, fontWeight: 600 }}>⏰ EXPIRED — ${qs.fob?.toLocaleString()}</span>
        <div style={{ fontSize: 9, color: 'var(--text3)' }}>Request new quote</div>
      </div>
    );
    if (qs?.status === 'priced' && qs.fob > 0) return (
      <div>
        <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: 12 }}>${qs.fob.toLocaleString()} ✅</span>
        {qs.expires_at && <div style={{ fontSize: 9, color: formatTimeLeft(qs.expires_at) === 'EXPIRED' ? 'var(--red)' : 'var(--text3)' }}>⏰ {formatTimeLeft(qs.expires_at)}</div>}
      </div>
    );
    if (qs?.status === 'requested') return <span style={{ color: 'var(--yellow)', fontSize: 10, fontWeight: 600 }}>FOB Requested 🟡</span>;
    return <span style={{ color: 'var(--text3)', fontSize: 10 }}>No price</span>;
  };

  // ══════════════════════════════════════════════════════════════
  // REVIEW PAGES
  // ══════════════════════════════════════════════════════════════

  // ── FOB Price Request Review ──
  if (showReview === 'fob') {
    return (
      <div>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowReview(false)} style={{ marginBottom: 16 }}>← Back to My Stock</button>
        <h2 style={{ marginBottom: 6 }}>Request FOB Prices</h2>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>You are requesting prices for these units:</p>

        {checkedItems.map(item => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8 }}>
            <div style={{ width: 44, height: 34, borderRadius: 6, overflow: 'hidden', background: 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.images?.length ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getIcon(item)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getName(item)}{item.year ? ` (${item.year})` : ''}</div>
              <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{item.sku}</div>
            </div>
            <button onClick={() => setCheckedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; })}
              style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, flexShrink: 0, padding: 4 }}>×</button>
          </div>
        ))}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <label className="form-label">Notes to seller (optional)</label>
          <textarea className="form-input" rows={3} value={reviewNote} onChange={e => setReviewNote(e.target.value)}
            placeholder="e.g. I need these urgently, please quote ASAP" style={{ resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setShowReview(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={checkedItems.length === 0} onClick={handleRequestFobPrices}>
            <DollarSignIcon /> Submit Price Request ({checkedItems.length} units)
          </button>
        </div>
      </div>
    );
  }

  // ── Final Selection Review ──
  if (showReview === 'selection') {
    const pricedItems = checkedItems.filter(i => getFob(i));
    const unpricedItems = checkedItems.filter(i => !getFob(i));
    return (
      <div>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowReview(false)} style={{ marginBottom: 16 }}>← Back to My Stock</button>
        <h2 style={{ marginBottom: 6 }}>Review Final Selection</h2>
        <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>Review your units before submitting for approval</p>

        {checkedItems.map(item => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8 }}>
            <div style={{ width: 44, height: 34, borderRadius: 6, overflow: 'hidden', background: 'var(--bg)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.images?.length ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getIcon(item)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{getName(item)}{item.year ? ` (${item.year})` : ''}</div>
              <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{item.sku}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>{renderPriceBadge(item)}</div>
            <button onClick={() => setCheckedIds(prev => { const n = new Set(prev); n.delete(item.id); return n; })}
              style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 16, flexShrink: 0, padding: 4 }}>×</button>
          </div>
        ))}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <label className="form-label">Notes to Admin (optional)</label>
          <textarea className="form-input" rows={3} value={reviewNote} onChange={e => setReviewNote(e.target.value)}
            placeholder="e.g. Please ship together with previous order" style={{ resize: 'vertical' }} />
        </div>

        <div style={{ marginTop: 20, padding: 16, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span>Total units</span><span style={{ fontWeight: 700 }}>{checkedItems.length}</span>
          </div>
          {pricedItems.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>Priced</span><span style={{ fontWeight: 700, color: 'var(--green)' }}>{pricedItems.length} (${checkedTotal.toLocaleString()})</span>
            </div>
          )}
          {unpricedItems.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--yellow)' }}>
              <span>Pending price</span><span style={{ fontWeight: 700 }}>{unpricedItems.length}</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn-secondary" onClick={() => setShowReview(false)}>← Back</button>
          <button className="btn btn-success" disabled={checkedItems.length === 0} onClick={handleSubmitSelection}>
            <CheckIcon /> Submit Final Selection
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // MAIN PAGE
  // ══════════════════════════════════════════════════════════════
  const pendingQuotes = myQuotes.filter(q => ['pending', 'in_progress', 'pending_approval', 'revision_needed'].includes(q.status) && Array.isArray(q.units));
  const completedQuotes = myQuotes.filter(q => q.status === 'approved' && Array.isArray(q.units));

  return (
    <>
      <div style={{ paddingBottom: checkedIds.size > 0 ? 80 : 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
          <h2>{t('myStock')}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { exportStockPDF(allItems, user.name, 'All Stock'); }}>📄 Download PDF</button>
            </div>
            <button className="btn btn-whatsapp" onClick={() => window.open('https://wa.me/971565665400')}><WhatsAppIcon />Contact Us</button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="tabs" style={{ marginBottom: 14 }}>
          <button className={`tab ${mainTab === 'stock' ? 'active' : ''}`} onClick={() => setMainTab('stock')}>
            My Stock<span className="cnt">{allItems.length}</span>
          </button>
          <button className={`tab ${mainTab === 'quotes' ? 'active' : ''}`} onClick={() => setMainTab('quotes')}>
            My Price Requests<span className="cnt">{pendingQuotes.length + completedQuotes.length}</span>
          </button>
          <button className={`tab ${mainTab === 'orders' ? 'active' : ''}`} onClick={() => setMainTab('orders')}>
            My Orders<span className="cnt">{myOrders.filter(o => o.status !== 'cancelled').length}</span>
          </button>
        </div>

        {submitted && (
          <div style={{ padding: '14px 18px', background: 'rgba(63,185,80,0.12)', border: '1px solid rgba(63,185,80,0.3)', borderRadius: 10, marginBottom: 16, color: 'var(--green)', fontSize: 13, textAlign: 'center' }}>
            {submitted}
            <button onClick={() => setSubmitted('')} style={{ background: 'none', border: 'none', color: 'var(--green)', cursor: 'pointer', marginLeft: 12, fontSize: 14 }}>×</button>
          </div>
        )}
        {offerError && (
          <div style={{ padding: '14px 18px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, marginBottom: 16, color: 'var(--red)', fontSize: 13, textAlign: 'center' }}>
            {offerError}
            <button onClick={() => setOfferError('')} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', marginLeft: 12, fontSize: 14 }}>×</button>
          </div>
        )}

        {/* ═══ STOCK TAB ═══ */}
        {mainTab === 'stock' && (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                Available Units <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>{availableUnits.length}</span>
              </div>

              {/* Sub-tabs + search */}
              <div className="tabs" style={{ marginBottom: 10 }}>
                {['all', 'vehicles', 'engines', 'parts'].map(t => (
                  <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); setSearch(''); }}>
                    {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
                    <span className="cnt">{t === 'all' ? availableUnits.length : availableUnits.filter(i => i._type === (t === 'parts' ? 'part' : t.slice(0,-1))).length}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div className="inv-search-wrap">
                  <span className="inv-search-icon"><SearchIcon /></span>
                  <input className="inv-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filteredItems.length} units</span>
              </div>

              {filteredItems.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30, fontSize: 13 }}>
                  {search ? 'No units match' : 'No available units. Browse inventory to add units.'}
                </div>
              ) : filteredItems.map(item => {
                const isChecked = checkedIds.has(item.id);
                const isMyUnit = item.assignedTo === user.id;
                const offerable = canCustomerOffer(item, user);
                const offerableStatus = ['available', 'japan_yard', 'sold_awaiting_payment'].includes(item.status);
                const outOfCountry = !offerable && !isMyUnit && offerableStatus && !!item.destination_country;
                const muted = !offerable && !isMyUnit;
                const statusLabel = outOfCountry
                  ? 'Reserved for another market'
                  : getCustomerStatusLabel(item, user.id);
                return (
                  <div key={item.id} className="stock-item" style={{ cursor: 'pointer', border: isChecked ? '1px solid var(--blue)' : undefined, background: isChecked ? 'rgba(88,166,255,0.05)' : undefined, opacity: muted ? 0.5 : 1 }}
                    onClick={() => setDetail({ item, _type: item._type, type: getDbKey(item._type) })}>
                    <div onClick={e => toggleCheck(item.id, e)} style={{ flexShrink: 0, cursor: 'pointer' }}>
                      <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${isChecked ? 'var(--blue)' : 'var(--border)'}`, background: isChecked ? 'var(--blue)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isChecked && <CheckIcon />}
                      </div>
                    </div>
                    <div className="stock-img">
                      {item.images?.length ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} /> : getIcon(item)}
                    </div>
                    <div className="stock-info">
                      <div className="stock-title">{getName(item)}</div>
                      <div className="stock-sku">{item.sku}</div>
                      <div className="stock-meta">
                        {item.year || item.condition || item.category}
                        {item.cutType && <span className="stock-cut">{CUT_TYPES.find(c => c.id === item.cutType)?.name}</span>}
                        <span style={{ marginLeft: 6 }}>{renderPriceBadge(item)}</span>
                      </div>
                      {(() => {
                        const activeOffer = getActiveOffer(item.id);
                        if (activeOffer) {
                          const cur = activeOffer.offerCurrency || 'USD';
                          if (activeOffer.status === 'pending') return (
                            <div style={{ marginTop: 4 }} onClick={e => e.stopPropagation()}>
                              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--yellow)' }}>
                                Offer sent: {cur} {activeOffer.offerAmount?.toLocaleString()} — awaiting approval
                              </span>
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: 9, padding: '2px 6px', marginLeft: 6 }}
                                disabled={offerSaving} onClick={() => withdrawOffer(activeOffer.id)}>Withdraw</button>
                            </div>
                          );
                          if (activeOffer.status === 'countered') return (
                            <div style={{ marginTop: 4 }} onClick={e => e.stopPropagation()}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--blue)' }}>
                                You offered {cur} {activeOffer.offerAmount?.toLocaleString()} · We countered {activeOffer.counterCurrency || cur} {activeOffer.counterAmount?.toLocaleString()}
                              </div>
                              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                                <button className="btn btn-success btn-sm" style={{ fontSize: 9, padding: '2px 6px' }}
                                  disabled={offerSaving} onClick={() => respondToCounter(activeOffer.id, true)}>Accept</button>
                                <button className="btn btn-secondary btn-sm" style={{ fontSize: 9, padding: '2px 6px', color: 'var(--red)' }}
                                  disabled={offerSaving} onClick={() => respondToCounter(activeOffer.id, false)}>Decline</button>
                              </div>
                            </div>
                          );
                          if (activeOffer.status === 'agreed') return (
                            <div style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)' }}>
                                Price agreed: {activeOffer.counterCurrency || cur} {(activeOffer.counterAmount || activeOffer.offerAmount)?.toLocaleString()}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <>
                            <div style={{ marginTop: 4 }}>
                              <span style={{
                                fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                                background: muted ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)',
                                color: muted ? 'var(--red, #ef4444)' : 'var(--green, #22c55e)',
                              }}>{statusLabel}</span>
                            </div>
                            {offerable && !activeOffer && (
                              <div onClick={e => e.stopPropagation()} style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <input type="number" placeholder="Your offer" min="1" step="any"
                                  value={offerAmounts[item.id] || ''}
                                  onChange={e => setOfferAmounts(prev => ({ ...prev, [item.id]: e.target.value }))}
                                  onClick={e => e.stopPropagation()}
                                  style={{ width: 110, height: 36, fontSize: 13, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} />
                                <select value={offerCur} onChange={e => setOfferCur(e.target.value)}
                                  style={{ height: 36, fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer' }}>
                                  {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <button className="btn btn-primary" style={{ fontSize: 12, padding: '7px 14px', borderRadius: 6 }}
                                  disabled={offerSaving || !offerAmounts[item.id] || parseFloat(offerAmounts[item.id]) <= 0}
                                  onClick={() => submitOffer(item, 'whole', [])}>Make Offer</button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      {offerable && item._type === 'vehicle' && !getActiveOffer(item.id) && (
                        <button className="btn btn-secondary btn-sm" title="Offer on parts" style={{ fontSize: 10, padding: '4px 8px' }}
                          onClick={() => { setOfferModal(item); setOfferParts([]); setOfferAmounts(prev => ({ ...prev, [item.id]: prev[item.id] || '' })); }}>
                          🔧 Parts
                        </button>
                      )}
                      {offerable && item._type === 'vehicle' && (
                        <button className="btn btn-secondary btn-sm" title="Request parts" style={{ fontSize: 10, padding: '4px 8px' }}
                          onClick={() => setPartsReqVehicle(item)}>
                          📋 Parts Request
                        </button>
                      )}
                    </div>
                    <button className="btn btn-cancel-unit" title="Remove from stock" onClick={e => openRemoveModal(item, e)}><XIcon /></button>
                  </div>
                );
              })}
            </div>

            {/* ── In Review ── */}
            {inReviewUnits.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--yellow)', marginBottom: 10 }}>
                  🟡 In Review <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>{inReviewUnits.length}</span>
                </div>
                {inReviewUnits.map(item => {
                  const info = lockedMap[item.id];
                  return (
                    <div key={item.id} className="stock-item" style={{ cursor: 'pointer', borderLeft: '3px solid var(--yellow)' }}
                      onClick={() => setDetail({ item, _type: item._type, type: getDbKey(item._type) })}>
                      <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: 'rgba(210,153,34,0.15)', color: 'var(--yellow)' }}>In Review</span>
                      <div className="stock-img">
                        {item.images?.length ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} /> : getIcon(item)}
                      </div>
                      <div className="stock-info">
                        <div className="stock-title">{getName(item)}</div>
                        <div className="stock-sku">{item.sku}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>Order #{info?.orderId}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Approved ── */}
            {approvedUnits.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', marginBottom: 10 }}>
                  ✅ Approved <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>{approvedUnits.length}</span>
                </div>
                {approvedUnits.map(item => {
                  const info = lockedMap[item.id];
                  const inv = getInvoiceForOrder(info?.orderId);
                  const cont = getContainerInfo(item.id);
                  return (
                    <div key={item.id} className="stock-item" style={{ cursor: 'pointer', borderLeft: '3px solid var(--green)' }}
                      onClick={() => setDetail({ item, _type: item._type, type: getDbKey(item._type) })}>
                      <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 10, background: 'rgba(63,185,80,0.15)', color: 'var(--green)' }}>Approved</span>
                      <div className="stock-img">
                        {item.images?.length ? <img src={item.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} /> : getIcon(item)}
                      </div>
                      <div className="stock-info">
                        <div className="stock-title">{getName(item)}</div>
                        <div className="stock-sku">{item.sku}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                          {inv && <span>Invoice #{inv.invoiceNumber} · </span>}
                          {cont && <span>Container {cont.number || 'Pending'} · </span>}
                          {!inv && !cont && 'Awaiting next steps'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ═══ PRICE REQUESTS TAB ═══ */}
        {mainTab === 'quotes' && (
          <>
            {pendingQuotes.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--yellow)', marginBottom: 10 }}>🟡 Pending ({pendingQuotes.length})</div>
                {pendingQuotes.map(q => {
                  const isExp = expandedQuote === q.id;
                  const statusLabel = q.status === 'pending' ? 'Waiting for pricing...' : q.status === 'in_progress' ? 'Being priced...' : q.status === 'pending_approval' ? 'Awaiting admin approval...' : 'Under revision...';
                  return (
                    <div key={q.id} className="card" style={{ marginBottom: 8 }}>
                      <div className="card-body" style={{ padding: '12px 16px', cursor: 'pointer' }} onClick={() => setExpandedQuote(isExp ? null : q.id)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: 700 }}>#{q.id}</span>
                            <span style={{ color: 'var(--text3)', marginLeft: 8, fontSize: 12 }}>{(q.units || []).length} units</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--yellow)' }}>{statusLabel}</div>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Requested {q.createdAt}</div>
                      </div>
                      {isExp && (
                        <div style={{ padding: '0 16px 12px', borderTop: '1px solid var(--border)' }}>
                          {(q.units || []).map((u, i) => (
                            <div key={u.unit_id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < q.units.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                              <span style={{ color: 'var(--text3)' }}>{u.unit_type === 'vehicle' ? <CarIcon /> : u.unit_type === 'engine' ? <EngineIcon /> : <PartsIcon />}</span>
                              <span style={{ flex: 1 }}>{u.unit_description}</span>
                              <span style={{ color: 'var(--text3)', fontFamily: 'monospace', fontSize: 10 }}>{u.unit_sku}</span>
                            </div>
                          ))}
                          {q.customer_notes && <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>"{q.customer_notes}"</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {completedQuotes.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)', marginBottom: 10 }}>✅ Completed ({completedQuotes.length})</div>
                {completedQuotes.map(q => {
                  const isExp = expandedQuote === q.id;
                  return (
                    <div key={q.id} className="card" style={{ marginBottom: 8 }}>
                      <div className="card-body" style={{ padding: '12px 16px', cursor: 'pointer' }} onClick={() => setExpandedQuote(isExp ? null : q.id)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: 700 }}>#{q.id}</span>
                            <span style={{ color: 'var(--text3)', marginLeft: 8, fontSize: 12 }}>{(q.units || []).length} units</span>
                          </div>
                          <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 600 }}>Priced ✅</span>
                        </div>
                      </div>
                      {isExp && (
                        <div style={{ padding: '0 16px 12px', borderTop: '1px solid var(--border)' }}>
                          {(q.units || []).map((u, i) => (
                            <div key={u.unit_id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < q.units.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                              <span>{u.unit_description}</span>
                              <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--green)' }}>
                                {u.pricing?.fob_price ? `$${u.pricing.fob_price.toLocaleString()}` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {pendingQuotes.length === 0 && completedQuotes.length === 0 && (
              <div className="empty"><DollarSignIcon /><div className="empty-title">No Price Requests</div><div className="empty-text">Select units from your stock and request FOB prices.</div></div>
            )}
          </>
        )}

        {/* ═══ ORDERS TAB ═══ */}
        {mainTab === 'orders' && (
          <>
            {myOrders.filter(o => o.status !== 'cancelled').length === 0 ? (
              <div className="empty"><PackageIcon /><div className="empty-title">No Orders Yet</div><div className="empty-text">Select units from your stock and submit a selection order.</div></div>
            ) : [...myOrders].filter(o => o.status !== 'cancelled').sort((a, b) => (b.submittedAt || b.createdAt || '').localeCompare(a.submittedAt || a.createdAt || '')).map(order => {
              const isExp = expandedOrder === order.id;
              const approved = (order.units || []).filter(u => u.status === 'approved').length;
              const rejected = (order.units || []).filter(u => u.status === 'rejected').length;
              const pending = (order.units || []).filter(u => u.status === 'pending').length;
              const inv = getInvoiceForOrder(order.id);
              return (
                <div key={order.id} className="card" style={{ marginBottom: 10 }}>
                  <div className="card-body" style={{ cursor: 'pointer', padding: '14px 20px' }} onClick={() => setExpandedOrder(isExp ? null : order.id)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 700 }}>Order #{order.id}</span>
                        <span className={`status status-${order.status === 'approved' ? 'available' : order.status === 'rejected' ? 'sold' : 'pending'}`} style={{ fontSize: 10, marginLeft: 8 }}>
                          {(order.status || '').replace(/_/g, ' ')}
                        </span>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                          {order.submittedAt ? new Date(order.submittedAt).toLocaleDateString() : order.createdAt} · {(order.units || []).length} units
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
                        {approved > 0 && <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ {approved}</span>}
                        {rejected > 0 && <span style={{ color: 'var(--red)', fontWeight: 600 }}>✕ {rejected}</span>}
                        {pending > 0 && <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>⏳ {pending}</span>}
                      </div>
                    </div>
                  </div>
                  {isExp && (
                    <div style={{ padding: '0 20px 14px', borderTop: '1px solid var(--border)' }}>
                      {(order.units || []).map((u, i) => (
                        <div key={u.unit_id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < order.units.length - 1 ? '1px solid var(--border)' : 'none', opacity: u.status === 'rejected' ? 0.5 : 1 }}>
                          <div style={{ color: 'var(--text3)', width: 18, flexShrink: 0 }}>{u.unit_type === 'vehicle' ? <CarIcon /> : u.unit_type === 'engine' ? <EngineIcon /> : <PartsIcon />}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{u.unit_description}</div>
                            <div style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'monospace' }}>{u.unit_sku}</div>
                          </div>
                          {u.fob_price_usd > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>${u.fob_price_usd.toLocaleString()}</span>}
                          <span style={{ fontSize: 10, fontWeight: 700, color: u.status === 'approved' ? 'var(--green)' : u.status === 'rejected' ? 'var(--red)' : 'var(--yellow)' }}>
                            {u.status === 'approved' ? '✓ Approved' : u.status === 'rejected' ? '✕ Rejected' : '⏳ Pending'}
                          </span>
                        </div>
                      ))}
                      {inv && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--blue)' }}>📄 Invoice: #{inv.invoiceNumber}</div>}
                      {order.customerNotes && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text3)' }}>Your notes: {order.customerNotes}</div>}
                      {order.status === 'submitted' && (
                        <button className="btn btn-secondary btn-sm" style={{ marginTop: 10, color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => cancelOrder(order.id)}>Cancel Order</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── Floating batch bar with TWO actions ── */}
      {checkedIds.size > 0 && mainTab === 'stock' && !showReview && (
        <div className="inv-batch-bar">
          <div>
            <span className="inv-batch-count">☑ {checkedIds.size} unit{checkedIds.size !== 1 ? 's' : ''} selected</span>
            {checkedTotal > 0 && <span style={{ color: 'var(--green)', marginLeft: 8, fontWeight: 700 }}>Total: ${checkedTotal.toLocaleString()}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={() => setCheckedIds(new Set())}>Clear</button>
            <button className="btn btn-primary" onClick={() => setShowReview('fob')}>
              <DollarSignIcon /> Request FOB Prices
            </button>
            <button className="btn btn-success" onClick={() => setShowReview('selection')}>
              <CheckIcon /> Submit Selection
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && <DetailModal item={detail.item} type={detail.type} onClose={() => setDetail(null)} currency={currency} canPrice={false} isCustomer={true} lang={lang} />}

      {/* Remove modal */}
      {cancelTarget && (
        <div className="modal-overlay" onClick={() => setCancelTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Remove from Stock</h3><button className="modal-close" onClick={() => setCancelTarget(null)}>×</button></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 12 }}>Remove this unit from your stock?</p>
              <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px', fontWeight: 700, marginBottom: 16 }}>
                {getName(cancelTarget)} <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>{cancelTarget.sku}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmRemove}><XIcon />Remove</button>
            </div>
          </div>
        </div>
      )}

      {partsReqVehicle && <PartsRequestModal vehicle={partsReqVehicle} user={user} onClose={() => setPartsReqVehicle(null)} onSave={() => forceUpdate({})} />}

      {/* Parts Offer Modal */}
      {offerModal && (
        <div className="modal-overlay" onClick={() => { setOfferModal(null); setOfferParts([]); }}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Offer on Parts — {getName(offerModal)}</h3>
              <button className="modal-close" onClick={() => { setOfferModal(null); setOfferParts([]); }}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: 400, overflowY: 'auto' }}>
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>Select parts you want, then enter one price for the bundle:</p>

              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>Cut Types</div>
              {CUT_TYPES.map(ct => (
                <label key={ct.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={offerParts.some(p => p.partTypeId === ct.id)}
                    onChange={e => {
                      if (e.target.checked) setOfferParts(prev => [...prev, { partTypeId: ct.id, quantity: 1 }]);
                      else setOfferParts(prev => prev.filter(p => p.partTypeId !== ct.id));
                    }} />
                  {ct.name}
                </label>
              ))}

              <div style={{ fontSize: 11, fontWeight: 700, marginTop: 12, marginBottom: 6, color: 'var(--text)' }}>Individual Parts</div>
              {OFFERABLE_PARTS.map(pt => (
                <label key={pt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={offerParts.some(p => p.partTypeId === pt.id)}
                    onChange={e => {
                      if (e.target.checked) setOfferParts(prev => [...prev, { partTypeId: pt.id, quantity: 1 }]);
                      else setOfferParts(prev => prev.filter(p => p.partTypeId !== pt.id));
                    }} />
                  {pt.display}
                </label>
              ))}

              <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <label className="form-label" style={{ fontSize: 12 }}>Your price for selected parts</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="number" className="form-input" min="1" step="any" placeholder="Enter amount"
                    value={offerAmounts[offerModal.id] || ''}
                    onChange={e => setOfferAmounts(prev => ({ ...prev, [offerModal.id]: e.target.value }))}
                    style={{ flex: 1 }} />
                  <select value={offerCur} onChange={e => setOfferCur(e.target.value)}
                    style={{ height: 40, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', cursor: 'pointer' }}>
                    {availableCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setOfferModal(null); setOfferParts([]); }}>Cancel</button>
              <button className="btn btn-primary"
                disabled={offerSaving || offerParts.length === 0 || !offerAmounts[offerModal.id] || parseFloat(offerAmounts[offerModal.id]) <= 0}
                onClick={() => submitOffer(offerModal, 'parts', offerParts)}>
                Submit Parts Offer ({offerParts.length} selected)
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MyStock;
